<script setup lang="ts">
import { computed, ref, toRefs, onMounted } from 'vue';
import FileUpload from 'primevue/fileupload';
import FilePreview from './components/FilePreview.vue';
import Dialog from 'primevue/dialog';
import useFileList from '@/composables/useFileList';
import { EMIT } from '@/enum';
import { FileInterface } from '@/types';
import { useDocumentsStore, useOrganizationStore, useBillingStore, useOrganizationDocumentStore } from '@/store';

interface PropsInterface {
  title: string;
  className?: string | null;
  loading?: boolean;
  billing?: boolean;
}

const emit = defineEmits<{
  (eventName: EMIT.FILE_DROPPED, files: string[]): void;
  (eventName: EMIT.SAVE): void;
  (eventName: EMIT.CANCEL): void;
}>();

const props = defineProps<PropsInterface>();
const documentStore = useDocumentsStore();
const billingStore = useBillingStore();
const organizationStore = useOrganizationStore();
const organizationDocumentStore = useOrganizationDocumentStore();
const { title, billing } = toRefs(props);
const { files, addFiles, removeFile } = useFileList();
const selectedInvoice = ref<string>();
const description = ref<string>('');
const invoices = ref<string[]>([]);
const modalVisible = ref(true);

const hasError = computed(() => !!documentStore.error);
onMounted(() => {
  getInvoiceNumbers();
});

const getInvoiceNumbers = async () => {
  await billingStore.getAllInvoices({ search: organizationStore.organization.id.toString() });
  invoices.value = billingStore.invoices
    ?.filter((invoice) => invoice.organization_id == organizationStore.organization.id)
    .map((invoice) => invoice.invoice_number) as string[];
};

/**
 * create the form data for proper POST
 * of blob documents
 */
const createForm = (file: FileInterface) => {
  const formData = new FormData();
  formData.append('document', file.file as Blob);
  formData.append('name', file.name);
  if (billing.value) {
    formData.append('invoice_number', selectedInvoice.value as string);
  } else {
    formData.append('description', description.value);
  }

  return formData;
};

/**
 * emit the SAVE to the parent with the files to upload,
 * reset the array and hide the modal
 * note: this only handles ONE file upload
 */
const save = async () => {
  const file: FileInterface = [...files.value][0];
  const formData = createForm(file);
  if (billing.value) {
    await organizationDocumentStore.uploadBillingDocument(organizationStore.organization.id, formData);
  } else {
    await organizationDocumentStore.uploadGeneralDocument(organizationStore.organization.id, formData);
  }

  if (!hasError.value) {
    modalVisible.value = false;
    emit(EMIT.SAVE);
  } else {
    removeFile(file);
  }
};

/**
 * on dropping the file to the uploader add to file storage
 */
const onDrop = (e: Event) => addFiles(e.files);

/**
 * get the form items and make sure theyre completed
 * before enabling the save button
 * @returns boolean
 */
const formDisabled = computed<boolean>(() => {
  const invoiceSelected = selectedInvoice.value || !billing.value;
  const descriptionFilled = description.value || billing.value;
  return !invoiceSelected || !descriptionFilled || ![...files.value].length;
});

const cancel = () => {
  removeError();
  emit(EMIT.CANCEL);
  modalVisible.value = false;
};

const removeError = () => (documentStore.error = undefined);
</script>

<template>
  <Dialog
    v-model:visible="modalVisible"
    class="dialog__container add-document__modal"
    :modal="true"
    :draggable="false"
    @hide="cancel"
  >
    <template #header>
      <div class="dialog__header flex flex-column">
        <h2 class="m-0" v-text="props.title" />
      </div>
    </template>
    <div class="upload-wrapper flex relative text-center align-items-center justify-content-center w-full">
      <FileUpload
        v-if="!files.length"
        :custom-upload="true"
        class="upload relative"
        choose-label="Choose file"
        accept=".pdf, .doc, .docx, .xls, .xlsx"
        :invalid-file-type-message="'Sorry, this file type is not supported. Please select a supported file type: {1}'"
        :auto="true"
        mode="advanced"
        :show-cancel-button="false"
        :show-upload-button="false"
        :max-file-size="100000000"
        invalid-file-size-message="Error: This file exceeds the max file size allowed."
        @uploader="onDrop"
      >
        <template #empty>
          <p class="uploader-text font-bold text-base mt-2">
            <span class="underline cursor-pointer">Choose File</span>
            or drag and drop here
          </p>
          <p class="acceptable-files-text">Allowed types: .pdf, .doc, .docx, .xls, .xlsx</p>
          <p class="acceptable-files-text">Max File size: 100MB</p>
        </template>
      </FileUpload>

      <ul v-else class="upload-wrapper__file-list flex w-full h-full list-none absolute left-0 align-items-center">
        <FilePreview
          v-for="file of files"
          :key="file.id"
          class="w-full flex text-left align-items-center"
          :file="file"
          tag="li"
          @remove="removeFile"
        />
      </ul>
    </div>
    <div v-if="billing" class="field w-full description-field required mt-4">
      <label for="invoice_number">Invoice Number</label>
      <Dropdown
        id="invoice_number"
        v-model="selectedInvoice"
        :options="invoices"
        required
        placeholder="Select an Invoice Number"
        class="description-field__input w-full"
      />
    </div>
    <div v-else class="field w-full description-field required mt-4">
      <label for="description">Description</label>
      <InputText
        id="description"
        v-model="description"
        required
        class="description-field__input w-full"
        name="description"
      />
    </div>
    <template #footer>
      <AppButton type="secondary" text="Cancel" @click="cancel" />
      <AppButton type="primary" :text="title" :disabled="formDisabled" @click="save" />
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.dialog__container {
  .p-dialog-content {
    padding-top: 0;

    .upload-wrapper {
      height: 98px;
      padding-top: 0;
      margin-top: 0;
      align-items: flex-start;

      :deep(.p-fileupload-buttonbar) {
        padding-top: 0;
        background: none;
        border: none;

        .p-button {
          margin-left: -77%;
          padding: 6px;
          font-weight: bold;
          cursor: pointer;
          z-index: 5;
          border: none;

          .p-button-label {
            text-decoration: underline;
            font-size: 16px;
          }
          .p-button-icon {
            display: none;
          }
        }
      }
      :deep(.p-fileupload-content) {
        text-align: center;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1;
        background: transparent;
        display: flex;
        justify-content: center;

        .p-message-error {
          position: absolute;
          top: -51px;
          width: 100%;
        }
        .p-fileupload-empty {
          position: relative;
          top: -10px;
          .uploader-text {
            color: var(--primary-color-dark);
            line-height: 18px;
            margin-bottom: 4px;
          }
          .acceptable-files-text {
            font-size: 10px;
            line-height: 12px;
            color: var(--primary-color-dark);
          }
        }
      }
      :deep(.p-fileupload-highlight) {
        border-color: var(--primary-color);
      }

      .upload-wrapper__file-list {
        color: $grayDark;
        font-size: 14px;
        line-height: 18px;
        border: 2px dashed $grayMedium;
        border-radius: 8px;
        height: 98px;
        padding: 1rem 2rem;
        top: -14px;
      }

      .v-enter-active,
      .v-leave-active {
        transition: opacity 0.5s ease;
      }
      .v-enter-from,
      .v-leave-to {
        opacity: 0;
      }
      .upload-wrapper__error {
        color: #822508;
        top: -65px;
        height: 37px;
        z-index: 6;
      }
    }

    .description-field {
      margin-top: 6px;
      margin-bottom: 6px;

      .description-field__input {
        margin: 2px 0;
      }
    }
  }
}
</style>
